// Social sign-in for the member login (Accedi) — mirrors the legacy site exactly: get a provider
// token client-side, then POST it to /security/login as `google_token` / `fb_token`. The backend
// (LoginAuthenticator + SocialLoginService) verifies the token and creates/links the member.
// Uses the SAME public client IDs as the legacy site, supplied via Vite env.

/* eslint-disable @typescript-eslint/no-explicit-any */
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined

export const googleConfigured = Boolean(GOOGLE_CLIENT_ID)
export const facebookConfigured = Boolean(FACEBOOK_APP_ID)

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve()
    const s = document.createElement('script')
    s.src = src
    s.id = id
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`load failed: ${src}`))
    document.head.appendChild(s)
  })
}

/* ── Google Identity Services → ID token (credential), sent as google_token ── */
export async function googleSignIn(): Promise<string> {
  if (!GOOGLE_CLIENT_ID) throw new Error('Google non configurato')
  await loadScript('https://accounts.google.com/gsi/client', 'gsi-client')
  const g = (window as any).google
  if (!g?.accounts?.id) throw new Error('Google SDK non disponibile')
  return new Promise<string>((resolve, reject) => {
    g.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (resp: { credential?: string }) =>
        resp?.credential ? resolve(resp.credential) : reject(new Error('Google: token mancante')),
    })
    g.accounts.id.prompt((n: any) => {
      // first settle wins, so a post-success dismissal is harmless
      if (n?.isNotDisplayed?.() || n?.isSkippedMoment?.() || n?.isDismissedMoment?.()) {
        reject(new Error('Accesso Google non disponibile (origine non autorizzata o popup bloccato).'))
      }
    })
  })
}

/* ── Facebook SDK → access token, sent as fb_token ── */
let fbReady = false
function fbInit(): Promise<void> {
  if (!FACEBOOK_APP_ID) return Promise.reject(new Error('Facebook non configurato'))
  if (fbReady) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    ;(window as any).fbAsyncInit = () => {
      ;(window as any).FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: false, version: 'v19.0' })
      fbReady = true
      resolve()
    }
    loadScript('https://connect.facebook.net/en_US/sdk.js', 'fb-sdk').catch(reject)
  })
}

export async function facebookSignIn(): Promise<string> {
  await fbInit()
  const FB = (window as any).FB
  return new Promise<string>((resolve, reject) => {
    FB.login(
      (resp: any) => {
        const tok = resp?.authResponse?.accessToken
        if (resp?.status === 'connected' && tok) resolve(tok)
        else reject(new Error('Accesso Facebook annullato'))
      },
      { scope: 'email,public_profile' },
    )
  })
}
